import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import './MultiArmedBanditDemo.css';

const ACTIONS_PER_SECOND = 20; // Controls simulation speed
const NUM_POINTS = 200;
const DEFAULT_DISCOUNT_FACTOR = 0.999; // Default discount factor (between 0 and 1)

const MultiArmedBanditDemo = () => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#FFD93D', 
    '#6C5CE7', '#A8E6CF', '#FF8B94'
  ];

  const [nextId, setNextId] = useState(4);
  const [arms, setArms] = useState([
    { 
      id: 1, 
      winRate: Math.random(), 
      color: colors[0],
      weightedSuccesses: 0,
      weightedFailures: 0,
      totalTrials: 0,
      selections: 0
    },
    { 
      id: 2, 
      winRate: Math.random(), 
      color: colors[1],
      weightedSuccesses: 0,
      weightedFailures: 0,
      totalTrials: 0,
      selections: 0
    },
    { 
      id: 3, 
      winRate: Math.random(), 
      color: colors[2],
      weightedSuccesses: 0,
      weightedFailures: 0,
      totalTrials: 0,
      selections: 0
    }
  ]);

  const [discountFactor, setDiscountFactor] = useState(DEFAULT_DISCOUNT_FACTOR);
  const [isRunning, setIsRunning] = useState(false);
  const [iterations, setIterations] = useState(0);
  const [cumulativeRegret, setCumulativeRegret] = useState([]);
  const [probabilitiesHistory, setProbabilitiesHistory] = useState([]);
  const plotRef = useRef(null);
  const regretPlotRef = useRef(null);
  const probabilitiesPlotRef = useRef(null);
  const animationFrameId = useRef(null);

  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [acquisitionFunction, setAcquisitionFunction] = useState('thompson');
  const [lastUpdateTime, setLastUpdateTime] = useState(0);

  useEffect(() => {
    // Initialize the histories when the component mounts
    resetHistories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetHistories = () => {
    setCumulativeRegret([{ iteration: 0, regret: 0 }]);
    
    const initialProbabilities = arms.map((arm, index) => ({
      armIndex: index,
      probability: 1 / arms.length, // Equal probability at start
      color: arm.color
    }));
    
    setProbabilitiesHistory([{
      iteration: 0,
      probabilities: initialProbabilities
    }]);
  };

  // Beta distribution helper functions
  const getBetaPDF = (x, a, b) => {
    if (x <= 0 || x >= 1) return 0;
    
    const beta = (x) => {
      return Math.exp(
        (a - 1) * Math.log(x) + 
        (b - 1) * Math.log(1 - x) - 
        (lnBeta(a, b))
      );
    };
    
    const lnBeta = (a, b) => {
      return Math.log(Math.exp(
        (a - 0.5) * Math.log(a) + 
        (b - 0.5) * Math.log(b) - 
        (a + b - 0.5) * Math.log(a + b) + 
        0.5 * Math.log(2 * Math.PI)
      ));
    };

    return beta(x);
  };

  const drawDistributions = () => {
    if (!plotRef.current) return;

    const margin = { top: 30, right: 20, bottom: 50, left: 40 }; // Increased bottom margin
    const width = plotRef.current.clientWidth - margin.left - margin.right;
    const height = 200 - margin.top - margin.bottom;

    // Clear previous SVG
    d3.select(plotRef.current).selectAll("*").remove();

    const svg = d3.select(plotRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale
    const x = d3.scaleLinear()
      .domain([0, 1])
      .range([0, width]);

    // Y scale
    const y = d3.scaleLinear()
      .range([height, 0]);

    // Generate points for each distribution
    const points = arms.map(arm => {
      const a = arm.weightedSuccesses + 1;
      const b = arm.weightedFailures + 1;
      
      const values = Array.from({length: NUM_POINTS}, (_, i) => 
        0.001 + (0.998 * i / (NUM_POINTS - 1))
      );
      
      // Calculate expected value (mean) instead of MAP
      const expectedValue = arm.totalTrials > 0 
        ? (arm.weightedSuccesses + 1) / (arm.weightedSuccesses + arm.weightedFailures + 2)
        : null;
        
      return values.map(x => ({
        x,
        y: getBetaPDF(x, a, b),
        color: arm.color,
        winRate: arm.winRate,
        observedWinRate: expectedValue,  // Using expected value here
        totalTrials: arm.totalTrials
      }));
    });

    // Update Y domain based on max density
    const maxDensity = d3.max(points.flat(), d => d.y);
    y.domain([0, maxDensity]);

    // Add true win rate markers and labels at the top
    arms.forEach((arm, index) => {
      // Add true win rate marker
      svg.append("line")
        .attr("x1", x(arm.winRate))
        .attr("x2", x(arm.winRate))
        .attr("y1", height)
        .attr("y2", 0)
        .attr("stroke", arm.color)
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "4,4");
      
      // Add label for true win rate at the top
      svg.append("text")
        .attr("x", x(arm.winRate))
        .attr("y", -10) // Position above the plot
        .attr("text-anchor", "middle")
        .attr("font-size", "10px")
        .attr("fill", arm.color)
        .text(`${(arm.winRate * 100).toFixed(1)}%`);
    });

    // Add distributions - just the line, no fill
    points.forEach((distribution, index) => {
      const line = d3.line()
        .x(d => x(d.x))
        .y(d => y(d.y))
        .curve(d3.curveBasis);

      // Add the line without area fill
      svg.append("path")
        .datum(distribution)
        .attr("fill", "none")
        .attr("stroke", distribution[0].color)
        .attr("stroke-width", 2)
        .attr("d", line);
      
      // Add observed win rate marker if we have trials
      if (distribution[0].observedWinRate !== null) {
        const observedRate = distribution[0].observedWinRate;
        
        // Observed win rate marker
        svg.append("line")
          .attr("x1", x(observedRate))
          .attr("x2", x(observedRate))
          .attr("y1", height)
          .attr("y2", height - 30)
          .attr("stroke", distribution[0].color)
          .attr("stroke-width", 2);
      }
    });

    // Add axes
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d => `${d*100}%`));

    // Add arm information labels with more space below
    if (arms.some(arm => arm.totalTrials > 0)) {
      arms.forEach((arm, index) => {
        if (arm.totalTrials > 0) {
          svg.append("text")
            .attr("x", 5 + (index * 120))
            .attr("y", height + 50) // More space below axis
            .attr("text-anchor", "start")
            .attr("font-size", "10px")
            .attr("fill", arm.color)
            .text(`Action ${index + 1}: ${arm.totalTrials} trials`);
        }
      });
    }
  };

  // Function to draw the cumulative regret plot
  const drawRegretPlot = () => {
    if (!regretPlotRef.current || cumulativeRegret.length < 2) return;

    const margin = { top: 10, right: 20, bottom: 30, left: 50 };
    const width = regretPlotRef.current.clientWidth - margin.left - margin.right;
    const height = 150 - margin.top - margin.bottom;

    // Clear previous SVG
    d3.select(regretPlotRef.current).selectAll("*").remove();

    const svg = d3.select(regretPlotRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale - iterations
    const x = d3.scaleLinear()
      .domain([0, d3.max(cumulativeRegret, d => d.iteration)])
      .range([0, width]);

    // Y scale - regret
    const y = d3.scaleLinear()
      .domain([0, d3.max(cumulativeRegret, d => d.regret) * 1.1]) // Add 10% margin
      .range([height, 0]);

    // Add regret line
    const line = d3.line()
      .x(d => x(d.iteration))
      .y(d => y(d.regret))
      .curve(d3.curveMonotoneX);

    svg.append("path")
      .datum(cumulativeRegret)
      .attr("fill", "none")
      .attr("stroke", "#E84A5F")
      .attr("stroke-width", 2)
      .attr("d", line);

    // Add axes
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5));

    svg.append("g")
      .call(d3.axisLeft(y).ticks(5));

    // Add axis labels
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text("Iterations");

    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -margin.left + 15)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text("Cumulative Regret");
  };

  // Function to draw the arm probabilities plot
  const drawProbabilitiesPlot = () => {
    if (!probabilitiesPlotRef.current || probabilitiesHistory.length < 2) return;

    const margin = { top: 10, right: 20, bottom: 30, left: 50 };
    const width = probabilitiesPlotRef.current.clientWidth - margin.left - margin.right;
    const height = 150 - margin.top - margin.bottom;

    // Clear previous SVG
    d3.select(probabilitiesPlotRef.current).selectAll("*").remove();

    const svg = d3.select(probabilitiesPlotRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale - iterations
    const x = d3.scaleLinear()
      .domain([0, d3.max(probabilitiesHistory, d => d.iteration)])
      .range([0, width]);

    // Y scale - probability [0, 1]
    const y = d3.scaleLinear()
      .domain([0, 1])
      .range([height, 0]);

    // Create one line per arm
    const numArms = arms.length;
    
    for (let armIndex = 0; armIndex < numArms; armIndex++) {
      // Extract data for this arm
      const armData = probabilitiesHistory.map(entry => ({
        iteration: entry.iteration,
        probability: entry.probabilities.find(p => p.armIndex === armIndex)?.probability || 0
      }));

      // Add line for this arm
      const line = d3.line()
        .x(d => x(d.iteration))
        .y(d => y(d.probability))
        .curve(d3.curveMonotoneX);

      svg.append("path")
        .datum(armData)
        .attr("fill", "none")
        .attr("stroke", arms[armIndex].color)
        .attr("stroke-width", 2)
        .attr("d", line);
    }

    // Add axes
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5));

    svg.append("g")
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${d*100}%`));

    // Add axis labels
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text("Iterations");

    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -margin.left + 15)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text("Probability of Being Optimal");
  };

  // Calculate probabilities of each arm being optimal using Monte Carlo sampling
  const calculateOptimalProbabilities = () => {
    const numSamples = 10000;
    const counts = Array(arms.length).fill(0);
    
    for (let i = 0; i < numSamples; i++) {
      const samples = arms.map(arm => {
        const a = arm.weightedSuccesses + 1;
        const b = arm.weightedFailures + 1;
        return rbeta(a, b);
      });
      
      const maxIndex = samples.indexOf(Math.max(...samples));
      counts[maxIndex]++;
    }
    
    return arms.map((arm, index) => ({
      armIndex: index,
      probability: counts[index] / numSamples,
      color: arm.color
    }));
  };

  // Correct Thompson sampling implementation
  const rbeta = (a, b) => {
    let x = 0;
    let y = 0;
    
    // Use gamma distribution approximation for more accurate sampling
    const sampleGamma = (alpha) => {
      if (alpha < 1) {
        const u = Math.random();
        return sampleGamma(1 + alpha) * Math.pow(u, 1 / alpha);
      }
      
      const d = alpha - 1/3;
      const c = 1 / Math.sqrt(9 * d);
      while (true) {
        let v = 0;
        while (v <= 0) {
          v = 1 + c * (Math.random() * 2 - 1);
        }
        const v3 = v * v * v;
        const u = Math.random();
        if (u < 1 - 0.0331 * Math.pow(v, 4) ||
            Math.log(u) < 0.5 * Math.pow(v, 2) + d * (1 - v3 + Math.log(v3))) {
          return d * v3;
        }
      }
    };

    x = sampleGamma(a);
    y = sampleGamma(b);
    
    return x / (x + y);
  };

  const runIteration = (timestamp) => {
    if (timestamp - lastUpdateTime < 1000 / ACTIONS_PER_SECOND) {
      animationFrameId.current = requestAnimationFrame(runIteration);
      return;
    }
    setLastUpdateTime(timestamp);

    setArms(currentArms => {
      // Find the best arm (highest true win rate)
      const bestWinRate = Math.max(...currentArms.map(arm => arm.winRate));
      
      let selectedArmIndex;
      
      // Choose arm based on acquisition function
      switch (acquisitionFunction) {
        case 'thompson':
          // Thompson sampling (existing implementation)
          const samples = currentArms.map(arm => {
            const a = arm.weightedSuccesses + 1;
            const b = arm.weightedFailures + 1;
            return rbeta(a, b);
          });
          selectedArmIndex = samples.indexOf(Math.max(...samples));
          break;
          
        case 'variance':
          // Posterior standard deviation (existing implementation)
          const stdDevs = currentArms.map(arm => {
            const a = arm.weightedSuccesses + 1;
            const b = arm.weightedFailures + 1;
            const variance = (a * b) / ((a + b) ** 2 * (a + b + 1));
            const stdDev = Math.sqrt(variance);
            return stdDev;
          });
          selectedArmIndex = stdDevs.indexOf(Math.max(...stdDevs));
          break;
          
        default:
          selectedArmIndex = 0;
      }

      // Calculate regret as the difference between expected rewards
      const instantRegret = bestWinRate - currentArms[selectedArmIndex].winRate;
      
      // Generate actual result for selected arm
      const selectedResult = Math.random() < currentArms[selectedArmIndex].winRate ? 1 : 0;
      
      // Update cumulative regret history
      setCumulativeRegret(prevRegret => {
        const lastRegret = prevRegret[prevRegret.length - 1].regret;
        return [...prevRegret, {
          iteration: iterations + 1,
          regret: lastRegret + instantRegret
        }];
      });
      
      // Calculate probabilities of being optimal
      if (iterations % 5 === 0) { // Update every 5 iterations to reduce computation
        const optimalProbs = calculateOptimalProbabilities();
        setProbabilitiesHistory(prev => [
          ...prev, 
          {
            iteration: iterations + 1,
            probabilities: optimalProbs
          }
        ]);
      }

      // First apply discount factor to ALL arms
      let updatedArms = currentArms.map(arm => ({
        ...arm,
        weightedSuccesses: arm.weightedSuccesses * discountFactor,
        weightedFailures: arm.weightedFailures * discountFactor
      }));
      
      // Update the selected arm with the actual result
      updatedArms[selectedArmIndex] = {
        ...updatedArms[selectedArmIndex],
        weightedSuccesses: updatedArms[selectedArmIndex].weightedSuccesses + (selectedResult ? 1 : 0),
        weightedFailures: updatedArms[selectedArmIndex].weightedFailures + (selectedResult ? 0 : 1),
        totalTrials: updatedArms[selectedArmIndex].totalTrials + 1,
        selections: updatedArms[selectedArmIndex].selections + 1
      };

      return updatedArms;
    });
    setIterations(prev => prev + 1);
  };

  useEffect(() => {
    if (isRunning) {
      animationFrameId.current = requestAnimationFrame(runIteration);
    }
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, iterations]);

  useEffect(() => {
    drawDistributions();
    if (cumulativeRegret.length > 1) {
      drawRegretPlot();
    }
    if (probabilitiesHistory.length > 1) {
      drawProbabilitiesPlot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arms, iterations, discountFactor, cumulativeRegret, probabilitiesHistory]);

  const handleStart = () => {
    setIsRunning(true);
    setHasStartedOnce(true);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setIterations(0);
    setArms(arms.map(arm => ({
      ...arm,
      weightedSuccesses: 0,
      weightedFailures: 0,
      totalTrials: 0,
      selections: 0
    })));
    
    // Clear the histories completely
    setCumulativeRegret([{ iteration: 0, regret: 0 }]);
    
    const initialProbabilities = arms.map((arm, index) => ({
      armIndex: index,
      probability: 1 / arms.length,
      color: arm.color
    }));
    
    setProbabilitiesHistory([{
      iteration: 0,
      probabilities: initialProbabilities
    }]);
  };

  const getNextAvailableColor = () => {
    const usedColors = new Set(arms.map(arm => arm.color));
    return colors.find(color => !usedColors.has(color));
  };

  const handleAddArm = () => {
    if (arms.length >= 6) return;
    const newArm = {
      id: nextId,
      winRate: Math.random(),
      color: getNextAvailableColor(),
      weightedSuccesses: 0,
      weightedFailures: 0,
      totalTrials: 0,
      selections: 0
    };
    setArms([...arms, newArm]);
    setNextId(nextId + 1);
    
    // Update probability history to include the new arm
    const initialProbabilities = [...arms, newArm].map((arm, index) => ({
      armIndex: index,
      probability: 1 / (arms.length + 1),
      color: arm.color
    }));
    
    setProbabilitiesHistory([{
      iteration: 0,
      probabilities: initialProbabilities
    }]);
  };

  const handleDeleteArm = (id) => {
    if (arms.length <= 2) return;
    const newArms = arms.filter(arm => arm.id !== id);
    setArms(newArms);
    
    // Reset histories when an arm is deleted
    resetHistories();
  };

  const handleWinRateChange = (id, newValue) => {
    setArms(arms.map(arm => 
      arm.id === id ? {...arm, winRate: newValue} : arm
    ));
  };

  const handleDiscountFactorChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 1) {
      setDiscountFactor(value);
    }
  };

  return (
    <div className="rl-grid-container">
        <h2>Multi-Armed Bandit Simulator</h2>

        <div className="arms-grid">
        {arms.map((arm, index) => (
            <div key={arm.id} className="arm-card">
            <div className="arm-header">
              <div className="arm-title">
                <h4>Action {index + 1}</h4>
                <div 
                  className="color-dot" 
                  style={{ backgroundColor: arm.color }}
                />
              </div>
              <button 
                className="delete-button"
                onClick={() => handleDeleteArm(arm.id)}
                disabled={arms.length <= 2}
                style={{ opacity: arms.length <= 2 ? 0.3 : 1 }}
              >
                ×
              </button>
            </div>
              <div className="win-rate-control">
                <div className="slider-container">
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.01" 
                    value={arm.winRate}
                  onChange={(e) => handleWinRateChange(arm.id, parseFloat(e.target.value))}
                  className="slider"
                  />
                  <span className="win-rate-value">{(arm.winRate * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        {arms.length < 6 && (
          <button 
            className="arm-card add-button" 
            onClick={handleAddArm}
          >
            +
          </button>
        )}
      </div>

      {hasStartedOnce && (
        <div className="plots-container">
          <div className="plot-section">
            <h3>Posterior Distributions</h3>
            <div ref={plotRef} className="main-plot"></div>
          </div>
          
          <div className="plot-section">
            <h3>Cumulative Regret</h3>
            <div ref={regretPlotRef} className="sub-plot"></div>
        </div>

          <div className="plot-section">
            <h3>Probability of Being Optimal</h3>
            <div ref={probabilitiesPlotRef} className="sub-plot"></div>
          </div>
        </div>
      )}

      <div className="rl-controls">
        <div className="rl-control-panel">
        {!isRunning ? (
            <button 
              className="rl-button rl-start-button"
              onClick={handleStart}
            >
              {hasStartedOnce ? "Continue" : "Start Optimization"}
            </button>
          ) : (
            <button 
              className="rl-button rl-stop-button"
              onClick={handlePause}
            >
              Pause
            </button>
          )}
          
          <button 
            className="rl-button rl-reset-button"
            onClick={handleReset}
          >
            Reset
          </button>
          <select 
            value={acquisitionFunction}
            onChange={(e) => setAcquisitionFunction(e.target.value)}
            className="rl-select"
          >
            <option value="thompson">Thompson Sampling</option>
            <option value="variance">Posterior Standard Deviation</option>
          </select>

          <div className="discount-control">
            <label htmlFor="discount-factor">Discount:</label>
            <input
              id="discount-factor"
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={discountFactor}
              onChange={handleDiscountFactorChange}
              className="discount-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiArmedBanditDemo; 